import React from 'react';

/**
 * Official token logos as inline SVG components.
 * Using inline SVGs guarantees crisp rendering at any size, zero network
 * requests, and no CORS issues.  Shapes are traced from the official
 * brand assets published by each project.
 */

/** Tether (USDT) – green circle with white "T" and horizontal bars */
const UsdtLogo = ({ size }) => (
  <svg width={size} height={size} viewBox="0 0 32 32" xmlns="http://www.w3.org/2000/svg">
    <circle cx="16" cy="16" r="16" fill="#26A17B" />
    <path
      d="M17.922 17.383v-.002c-.11.008-.677.042-1.942.042-1.01 0-1.721-.03-1.971-.042v.003c-3.888-.171-6.79-.848-6.79-1.658 0-.809 2.902-1.486 6.79-1.66v2.644c.254.018.982.061 1.988.061 1.207 0 1.812-.05 1.925-.06v-2.643c3.88.173 6.775.85 6.775 1.658 0 .81-2.895 1.485-6.775 1.657m0-3.59v-2.366h5.414V7.819H8.595v3.608h5.414v2.365c-4.4.202-7.709 1.074-7.709 2.118 0 1.044 3.309 1.915 7.709 2.118v7.582h3.913v-7.584c4.393-.202 7.694-1.073 7.694-2.116 0-1.043-3.301-1.914-7.694-2.117"
      fill="#fff"
    />
  </svg>
);

/** USD Coin (USDC) – official mark: USDC blue circle, "$" and symmetric arcs */
const UsdcLogo = ({ size }) => (
  <svg width={size} height={size} viewBox="0 0 2000 2000" xmlns="http://www.w3.org/2000/svg">
    <path
      d="M1000 2000c554.17 0 1000-445.83 1000-1000S1554.17 0 1000 0 0 445.83 0 1000s445.83 1000 1000 1000z"
      fill="#2775CA"
    />
    <path
      d="M1275 1158.33c0-145.83-87.5-195.83-262.5-216.66-125-16.67-150-50-150-108.34s41.67-95.83 125-95.83c75 0 116.67 25 137.5 87.5 4.17 12.5 16.67 20.83 29.17 20.83h66.66c16.67 0 29.17-12.5 29.17-29.16v-4.17c-16.67-91.67-91.67-162.5-187.5-170.83v-100c0-16.67-12.5-29.17-33.33-33.34h-62.5c-16.67 0-29.17 12.5-33.34 33.34v95.83c-125 16.67-204.16 100-204.16 204.17 0 137.5 83.33 191.66 258.33 212.5 116.67 20.83 154.17 45.83 154.17 112.5s-58.34 112.5-137.5 112.5c-108.34 0-145.84-45.84-158.34-108.34-4.16-16.66-16.66-25-29.16-25h-70.84c-16.66 0-29.16 12.5-29.16 29.17v4.17c16.66 104.16 83.33 179.16 220.83 200v100c0 16.66 12.5 29.16 33.33 33.33h62.5c16.67 0 29.17-12.5 33.34-33.33v-100c125-20.84 208.33-108.34 208.33-220.84z"
      fill="#fff"
    />
    <path
      d="M787.5 1595.83c-325-116.66-491.67-479.16-370.83-800 62.5-175 200-308.33 370.83-370.83 16.67-8.33 25-20.83 25-41.67V325c0-16.67-8.33-29.17-25-33.33-4.17 0-12.5 0-16.67 4.16-395.83 125-612.5 545.84-487.5 941.67 75 233.33 254.17 412.5 487.5 487.5 16.67 8.33 33.34 0 37.5-16.67 4.17-4.16 4.17-8.33 4.17-16.66v-58.34c0-12.5-12.5-29.16-25-37.5zM1229.17 295.83c-16.67-8.33-33.34 0-37.5 16.67-4.17 4.17-4.17 8.33-4.17 16.67v58.33c0 16.67 12.5 33.33 25 41.67 325 116.66 491.67 479.16 370.83 800-62.5 175-200 308.33-370.83 370.83-16.67 8.33-25 20.83-25 41.67V1700c0 16.67 8.33 29.17 25 33.33 4.17 0 12.5 0 16.67-4.16 395.83-125 612.5-545.84 487.5-941.67-75-237.5-258.34-416.67-487.5-491.67z"
      fill="#fff"
    />
  </svg>
);

/** Dai (DAI) – gold/yellow circle with white Dai maker logo */
const DaiLogo = ({ size }) => (
  <svg width={size} height={size} viewBox="0 0 32 32" xmlns="http://www.w3.org/2000/svg">
    <circle cx="16" cy="16" r="16" fill="#F5AC37" />
    <path
      d="M16.155 6L9 15.86h4.55L16.155 11l2.606 4.86H23.3L16.155 6zm0 20l-7.144-9.86h4.55l2.594 4.856 2.606-4.856h4.538L16.155 26z"
      fill="#fff"
    />
    <rect x="7" y="14.5" width="18" height="3" rx="0.5" fill="#fff" />
  </svg>
);

/**
 * USDN – Nova USD logo using the official Nova Starburst SVG mark.
 */
const UsdnLogo = ({ size }) => (
  <img
    src="/nova-starburst.svg"
    alt="USDN"
    width={size}
    height={size}
    style={{ objectFit: 'contain' }}
  />
);

/**
 * Mapping from token symbol to its logo component.
 */
const LOGO_MAP = {
  USDT: UsdtLogo,
  USDC: UsdcLogo,
  DAI: DaiLogo,
  USDN: UsdnLogo,
};

/**
 * TokenIcon – renders an official token logo at the given size.
 *
 * Falls back to a coloured circle with the token's initial letter
 * if no logo is mapped for the symbol.
 *
 * @param {Object} props
 * @param {string} props.symbol - Token symbol (e.g. "USDT", "USDN")
 * @param {number} [props.size=28] - Icon diameter in px
 * @param {string} [props.className] - Additional CSS class
 */
export const TokenIcon = ({ symbol, size = 28, className = '' }) => {
  const Logo = LOGO_MAP[symbol];

  if (Logo) {
    return (
      <span
        className={`token-icon-wrapper ${className}`}
        style={{ width: size, height: size, display: 'inline-flex', flexShrink: 0 }}
        aria-hidden="true"
      >
        <Logo size={size} />
      </span>
    );
  }

  // Fallback: coloured circle with first letter
  return (
    <span
      className={`token-icon ${className}`}
      style={{ width: size, height: size }}
      aria-hidden="true"
    >
      {symbol.charAt(0)}
    </span>
  );
};
